/**
 * Prioridades operacionais — até 3 itens com criticidade e responsável.
 */
import React, { useMemo } from 'react';
import { ChevronRight, UserX, Zap } from 'lucide-react';
import type { OpsActiveWorkflow, OpsTeamMember } from '../../../services/operationsCenterService';
import { EXEC_HERO_CLASS } from './opsExecutiveShared';

export interface OperationalPriorityItem {
  id: string;
  title: string;
  criticality: 'critical' | 'warning' | 'info';
  idleTime: string;
  staleLabel: string;
  slaImpact: string;
  responsible: string | null;
  onClick: () => void;
}

interface OpsOperationalPrioritiesPanelProps {
  stalledWorkflows: OpsActiveWorkflow[];
  teamWorkload: OpsTeamMember[];
  slaAtRisk: boolean;
  slaBreachedCount: number;
  slaCompliance: number | null;
  onSlaClick: () => void;
  onWorkflowClick: (id: string) => void;
}

function formatIdle(minutes: number | null | undefined): string {
  if (minutes == null || minutes <= 0) return '—';
  if (minutes < 60) return `${Math.round(minutes)}min parado`;
  if (minutes < 1440) return `${(minutes / 60).toFixed(1)}h parado`;
  return `${Math.floor(minutes / 1440)}d parado`;
}

function formatStale(minutes: number | null | undefined): string {
  if (minutes == null || minutes <= 0) return 'Sem movimentação registrada';
  if (minutes < 60) return `${Math.round(minutes)}min sem atualização`;
  if (minutes < 1440) return `${(minutes / 60).toFixed(1)}h sem atualização`;
  return `${Math.floor(minutes / 1440)}d sem atualização`;
}

function resolveResponsible(wf: OpsActiveWorkflow, team: OpsTeamMember[]): string | null {
  const uid = wf.assigned_to_users[0];
  if (!uid) return null;
  return team.find((m) => m.user_id === uid)?.user_name ?? null;
}

function workflowCriticality(wf: OpsActiveWorkflow): OperationalPriorityItem['criticality'] {
  if (wf.is_overdue || (wf.sla_remaining_minutes != null && wf.sla_remaining_minutes <= 0)) return 'critical';
  if (wf.status === 'on_hold' || (wf.sla_remaining_minutes != null && wf.sla_remaining_minutes <= 30)) return 'warning';
  return 'info';
}

function slaImpactLabel(wf: OpsActiveWorkflow): string {
  if (wf.is_overdue || (wf.sla_remaining_minutes != null && wf.sla_remaining_minutes <= 0)) return 'SLA estourado';
  if (wf.sla_remaining_minutes != null && wf.sla_remaining_minutes <= 30) return `SLA ${wf.sla_remaining_minutes}min`;
  return 'Em monitoramento';
}

const CRIT_BADGE: Record<OperationalPriorityItem['criticality'], string> = {
  critical: 'bg-red-500/15 text-red-600',
  warning: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  info: 'bg-sky-500/15 text-sky-700 dark:text-sky-400',
};

const CRIT_LABEL: Record<OperationalPriorityItem['criticality'], string> = {
  critical: 'Crítico',
  warning: 'Atenção',
  info: 'Monitorar',
};

export function buildOperationalPriorities(
  props: OpsOperationalPrioritiesPanelProps,
): OperationalPriorityItem[] {
  const staleMinutes = (wf: OpsActiveWorkflow) => wf.waiting_minutes ?? wf.elapsed_minutes ?? 0;

  const items: OperationalPriorityItem[] = props.stalledWorkflows
    .slice()
    .sort((a, b) => {
      const score = (w: OpsActiveWorkflow) =>
        (w.is_overdue ? 100 : 0)
        + (w.sla_remaining_minutes != null && w.sla_remaining_minutes <= 0 ? 50 : 0)
        + staleMinutes(w) / 60;
      return score(b) - score(a);
    })
    .slice(0, 3)
    .map((wf) => ({
      id: wf.id,
      title: wf.title,
      criticality: workflowCriticality(wf),
      idleTime: formatIdle(staleMinutes(wf)),
      staleLabel: formatStale(staleMinutes(wf)),
      slaImpact: slaImpactLabel(wf),
      responsible: resolveResponsible(wf, props.teamWorkload),
      onClick: () => props.onWorkflowClick(wf.id),
    }));

  if (items.length < 3 && props.slaAtRisk) {
    items.push({
      id: 'sla-risk',
      title: `SLA operacional — ${props.slaBreachedCount} execuções estouradas`,
      criticality: (props.slaCompliance ?? 100) < 80 ? 'critical' : 'warning',
      idleTime: 'Fila acumulando',
      staleLabel: 'Pressão contínua no cumprimento',
      slaImpact: `${props.slaCompliance ?? 0}% cumprimento`,
      responsible: null,
      onClick: props.onSlaClick,
    });
  }

  return items.slice(0, 3);
}

export const OpsOperationalPrioritiesPanel: React.FC<OpsOperationalPrioritiesPanelProps> = ({
  stalledWorkflows,
  teamWorkload,
  slaAtRisk,
  slaBreachedCount,
  slaCompliance,
  onSlaClick,
  onWorkflowClick,
}) => {
  const priorities = useMemo(
    () =>
      buildOperationalPriorities({
        stalledWorkflows,
        teamWorkload,
        slaAtRisk,
        slaBreachedCount,
        slaCompliance,
        onSlaClick,
        onWorkflowClick,
      }),
    [stalledWorkflows, teamWorkload, slaAtRisk, slaBreachedCount, slaCompliance, onSlaClick, onWorkflowClick],
  );

  if (priorities.length === 0) return null;

  const hasCritical = priorities.some((item) => item.criticality === 'critical');

  return (
    <div className={`${EXEC_HERO_CLASS} flex min-h-0 flex-col border-l-[3px] ${hasCritical ? 'border-l-red-500' : 'border-l-amber-500/80'}`}>
      <div className="flex shrink-0 items-center gap-2 border-b border-[var(--border-default)]/60 bg-amber-500/[0.06] px-3 py-2">
        <Zap size={14} className="text-amber-600" strokeWidth={2.25} />
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-800 dark:text-slate-100">
          Prioridades Operacionais
        </span>
        {priorities.length > 0 && (
          <span className="ml-auto rounded-full bg-amber-500/20 px-1.5 py-px text-[9px] font-bold tabular-nums text-amber-700">
            {priorities.length}
          </span>
        )}
      </div>

      <div className="min-h-0 flex-1 divide-y divide-[var(--border-default)]/50 overflow-hidden">
        {priorities.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={item.onClick}
              className="group flex w-full cursor-pointer flex-col gap-1 px-3 py-2.5 text-left transition-colors hover:bg-[var(--button-hover)]/50"
            >
              <div className="flex items-center gap-2">
                <span className={`shrink-0 rounded-sm px-1.5 py-0.5 text-[7px] font-bold uppercase ${CRIT_BADGE[item.criticality]}`}>
                  {CRIT_LABEL[item.criticality]}
                </span>
                <span className="truncate text-[8px] font-medium text-[var(--text-muted)]">{item.idleTime}</span>
                <ChevronRight size={11} className="ml-auto shrink-0 text-[var(--text-muted)] opacity-0 group-hover:opacity-100" />
              </div>
              <p className="line-clamp-1 text-[12px] font-semibold text-[var(--text-primary)]">{item.title}</p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[9px] text-[var(--text-secondary)]">
                <span>{item.slaImpact}</span>
                <span>{item.staleLabel}</span>
                <span className="flex items-center gap-1">
                  {item.responsible ? (
                    <span className="font-semibold text-[var(--text-primary)]">{item.responsible}</span>
                  ) : (
                    <>
                      <UserX size={10} className="text-[var(--text-muted)]" />
                      <span>Sem responsável</span>
                    </>
                  )}
                </span>
              </div>
            </button>
          ))}
      </div>
    </div>
  );
};

export default OpsOperationalPrioritiesPanel;
